/**
 * CandidateAssessmentsPage
 * Route: /candidate/assessments
 *
 * Landing page after candidate login. Lists every actionable
 * participation (Completed/Closed/Not Invited rows are excluded) and
 * lets the candidate pick which test to work on. When the candidate has
 * exactly one actionable participation, auto-redirects to the per-test
 * Welcome page; the list is only shown when there are multiple choices.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardX, Play, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { useActiveTestStore } from '../../stores/activeTestStore';
import { PageSpinner } from '../../components/PageSpinner';
import { EmptyState } from '../../components/EmptyState';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { SCENARIOS, isClientTPScenario, type ScenarioId } from '../../constants/scenarios';

interface AssessmentRow {
  test: {
    id: string;
    name?: string;
    due_date?: string;
    scenario_type?: string;
  } | null;
  participant?: { id?: string; status?: string };
  scenario_type?: string;
  scenario?: { id?: string; name?: string; skip_tp?: boolean };
  status: string;
  progress: { answered: number; total: number };
  is_completed: boolean;
  tp_submitted: boolean;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  'In Progress': 'bg-green-50 text-green-700 border-green-300',
  Paused: 'bg-amber-50 text-amber-800 border-brand-warning/50',
  Invited: 'bg-blue-50 text-blue-800 border-blue-200',
  'Awaiting Client Input': 'bg-blue-50 text-blue-800 border-blue-200',
  Provisioned: 'bg-brand-panel text-brand-steel/70 border-brand-steel/30',
};

function StatusBadge({ status }: { status: string }) {
  const classes =
    STATUS_BADGE_CLASSES[status] ?? 'bg-brand-panel text-brand-steel border-brand-steel/30';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes}`}
    >
      {status}
    </span>
  );
}

function ctaLabelFor(row: AssessmentRow): { label: string; isResume: boolean } {
  const aqStarted = row.progress.answered > 0;
  const isResume = aqStarted || row.tp_submitted;
  return { label: isResume ? 'Continue' : 'Start', isResume };
}

/**
 * Route a candidate from the list to the right next page.
 *
 * First-timers land on the Welcome screen for the test-context debrief.
 * Returning candidates (TP done or any AQ answered) skip Welcome and go
 * straight to where they left off — TP for Sc.1-3 candidates who haven't
 * finished allocations, AQ otherwise. The AQ page already renders the
 * Sc.4/5 "waiting on your company" state, so first-timers on a still-
 * gated test also flow through Welcome → wait screen, never the list →
 * AQ shortcut.
 */
export function nextRouteFor(row: AssessmentRow): string {
  const aqStarted = row.progress.answered > 0;
  const hasProgress = aqStarted || row.tp_submitted;
  if (!hasProgress) return '/candidate/welcome';
  const skipTP = isClientTPScenario(row.scenario)
    || isClientTPScenario(row.scenario_type ?? null);
  if (row.tp_submitted || skipTP) return '/candidate/aq';
  return '/candidate/tp';
}

function formatDueDate(due?: string): string | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return due;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function isDueDatePast(due?: string): boolean {
  if (!due) return false;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export function CandidateAssessmentsPage() {
  const navigate = useNavigate();
  const setTestId = useActiveTestStore((s) => s.setTestId);

  const [rows, setRows] = useState<AssessmentRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<AssessmentRow[]>('/api/candidate/assessments')
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load assessments');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-redirect: exactly one actionable row → land where the candidate
  // would land if they had clicked Continue. Welcome for first-timers,
  // TP/AQ for resumers — saves the returning candidate a stale debrief.
  useEffect(() => {
    if (isLoading || !rows) return;
    if (rows.length === 1 && rows[0].test?.id && !isDueDatePast(rows[0].test.due_date)) {
      setTestId(rows[0].test.id);
      navigate(nextRouteFor(rows[0]), { replace: true });
    }
  }, [isLoading, rows, navigate, setTestId]);

  const handleOpen = (row: AssessmentRow) => {
    if (!row.test?.id) return;
    setTestId(row.test.id);
    navigate(nextRouteFor(row));
  };

  if (isLoading) {
    return <PageSpinner label="Loading assessments" />;
  }

  if (error) {
    return (
      <main className="max-w-3xl mx-auto py-12 px-4">
        <div
          role="alert"
          className="p-4 bg-red-50 border border-brand-error/40 rounded-lg text-sm text-brand-error"
        >
          {error}
        </div>
      </main>
    );
  }

  // Auto-redirect in flight — keep spinner up so we don't flash the list.
  // Skip if the single row is expired (no redirect will happen).
  if (rows && rows.length === 1 && !isDueDatePast(rows[0]?.test?.due_date)) {
    return <PageSpinner label="Loading assessment" />;
  }

  if (!rows || rows.length === 0) {
    return (
      <main className="max-w-3xl mx-auto py-12 px-4">
        <EmptyState
          icon={ClipboardX}
          title="No assessments to work on"
          description="You don't have any active assessments right now. Your test administrator will let you know when one is ready for you."
        />
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto py-12 px-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Your assessments</h1>
      <p className="text-sm text-brand-steel mb-6">
        Pick an assessment to continue. You can switch between them at any time.
      </p>

      <div className="space-y-3">
        {rows.map((row) => {
          if (!row.test?.id) return null;
          const { label, isResume } = ctaLabelFor(row);
          const due = formatDueDate(row.test.due_date);
          const expired = isDueDatePast(row.test.due_date);
          const aqStarted = row.progress.answered > 0;
          const pct =
            row.progress.total > 0
              ? Math.min(100, Math.round((row.progress.answered / row.progress.total) * 100))
              : 0;

          return (
            <Card
              key={row.test.id}
              className={`border-brand-steel/50 bg-white shadow-sm${expired ? ' opacity-75' : ''}`}
            >
              <CardContent className="px-5 py-1">
                <div className="py-4 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 truncate">
                        {row.test.name || 'Untitled assessment'}
                      </h2>
                      <p className="text-xs text-brand-steel mt-1">
                        {row.scenario_type ? (SCENARIOS[row.scenario_type as ScenarioId]?.name ?? row.scenario_type) : '—'}
                        {due ? ` · Due ${due}` : ''}
                      </p>
                    </div>
                    {expired ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200">
                        <AlertTriangle size={12} aria-hidden="true" />
                        Expired
                      </span>
                    ) : (
                      <StatusBadge status={row.status} />
                    )}
                  </div>

                  {expired && (
                    <p className="text-xs text-brand-error">
                      This assessment's due date has passed. Contact your administrator if you need an extension.
                    </p>
                  )}

                  {!expired && aqStarted && row.progress.total > 0 && (
                    <div className="flex items-center gap-3">
                      <Progress
                        value={pct}
                        className="flex-1 h-2 bg-brand-steel/15 [&>div]:bg-brand-crimson"
                      />
                      <span className="text-xs text-brand-steel/70 whitespace-nowrap">
                        {row.progress.answered} / {row.progress.total} answered
                      </span>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={() => handleOpen(row)}
                      disabled={expired}
                      className="inline-flex items-center gap-2"
                    >
                      {isResume ? (
                        <ChevronRight size={16} aria-hidden="true" />
                      ) : (
                        <Play size={16} aria-hidden="true" />
                      )}
                      {label}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </main>
  );
}

export default CandidateAssessmentsPage;
